<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@/app/i18n'
import { useColorScheme } from '@/app/theme'
import AppIcon from '@/shared/ui/AppIcon.vue'

/**
 * The navbar's light/dark switch: a pill with a moon and a sun, the knob sliding under the active one (right = light,
 * left = dark) with a little overshoot; the icon it lands on turns in. A `switch` whose checked state is "dark".
 */
const { t } = useI18n()
const { scheme, toggle } = useColorScheme()
const dark = computed(() => scheme.value === 'dark')
</script>

<template>
  <button
    type="button"
    role="switch"
    class="theme-switch"
    :class="{ 'is-dark': dark }"
    :aria-checked="dark"
    :aria-label="t('shell.navbar.action.darkMode')"
    data-testid="theme-switch"
    @click="toggle"
  >
    <span class="theme-switch__knob" aria-hidden="true"></span>
    <span class="theme-switch__cell theme-switch__cell--dark" aria-hidden="true">
      <AppIcon name="MoonIcon" :size="16" class="theme-switch__icon" />
    </span>
    <span class="theme-switch__cell theme-switch__cell--light" aria-hidden="true">
      <AppIcon name="SunIcon" :size="16" class="theme-switch__icon" />
    </span>
  </button>
</template>

<style scoped>
.theme-switch {
  --switch-cell: 34px;
  --switch-inset: 3px;

  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  width: calc(2 * var(--switch-cell) + 2 * var(--switch-inset));
  height: 40px;
  padding: var(--switch-inset);
  cursor: pointer;
  background: var(--app-fill);
  border: 0;
  border-radius: 20px;
  transition: background 0.2s ease;
}
.theme-switch:hover {
  background: var(--app-fill-hover);
}
.theme-switch:focus-visible {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 2px;
}
.theme-switch__knob {
  position: absolute;
  top: var(--switch-inset);
  left: var(--switch-inset);
  width: var(--switch-cell);
  height: var(--switch-cell);
  background: var(--p-surface-0);
  border-radius: 50%;
  box-shadow: 0 2px 6px rgb(34 41 47 / 18%);
  /* Light: the knob sits under the sun, on the right. A touch of overshoot as it lands. */
  transform: translateX(var(--switch-cell));
  transition: transform 0.45s cubic-bezier(0.34, 1.4, 0.64, 1);
}
.theme-switch.is-dark .theme-switch__knob {
  transform: translateX(0);
}
.theme-switch__cell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--switch-cell);
  height: var(--switch-cell);
  color: var(--p-surface-500);
  transition: color 0.3s ease;
}
.theme-switch__icon {
  transition: transform 0.45s cubic-bezier(0.34, 1.4, 0.64, 1);
}
/* The icon the knob leaves tilts away; the one it reaches turns upright. */
.theme-switch__cell--light {
  color: var(--p-surface-900);
}
.theme-switch__cell--dark .theme-switch__icon {
  transform: rotate(-30deg) scale(0.9);
}
.theme-switch.is-dark .theme-switch__cell--dark {
  color: var(--p-surface-900);
}
.theme-switch.is-dark .theme-switch__cell--dark .theme-switch__icon {
  transform: none;
}
.theme-switch.is-dark .theme-switch__cell--light {
  color: var(--p-surface-500);
}
.theme-switch.is-dark .theme-switch__cell--light .theme-switch__icon {
  transform: rotate(60deg) scale(0.9);
}
@media (prefers-reduced-motion: reduce) {
  .theme-switch__knob,
  .theme-switch__icon,
  .theme-switch__cell {
    transition: none;
  }
}
</style>
